import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Loader2, Info, AlertTriangle } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useWhatsappEmbeddedSignup } from '@/frontend/hooks/useWhatsappEmbeddedSignup'
import { formatWhatsApp } from '@/frontend/utils/whatsappMask'
import type { DigitalWorkerDetail, WhatsAppUnderlyingProvider } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

type SharedInertiaProps = {
  featureFlags?: {
    default_whatsapp_underlying_provider?: WhatsAppUnderlyingProvider
  }
}

const WHATSAPP_ICON_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z'

export interface WhatsappEmbeddedSignupConfig {
  ready: boolean
  facebookAppId: string | null
  configId: string | null
  apiVersion: string
  digitalWorkerId?: string | null
  returnTo?: string
}

interface WhatsAppChannelCardProps {
  worker: DigitalWorkerDetail
  whatsapp_business_available: boolean
  whatsapp_embedded_signup: WhatsappEmbeddedSignupConfig
  can_manage_resources: boolean
}

function WhatsAppIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d={WHATSAPP_ICON_PATH} />
    </svg>
  )
}

const TERMINAL_STATUSES = ['verified', 'failed', 'not_applicable']

export default function WhatsAppChannelCard({
  worker,
  whatsapp_business_available,
  whatsapp_embedded_signup,
  can_manage_resources,
}: WhatsAppChannelCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const sharedProps = usePage<SharedInertiaProps>().props
  const defaultUnderlyingProvider = sharedProps.featureFlags?.default_whatsapp_underlying_provider ?? 'salvy'
  const underlyingProvider = (worker.whatsapp_underlying_provider ?? defaultUnderlyingProvider) as WhatsAppUnderlyingProvider

  const isBusiness = worker.whatsapp_provider === 'business'
  const isPersonal = worker.whatsapp_provider === 'personal'
  const isNone = worker.whatsapp_provider === 'none'
  const isReady = worker.provisioning_status === 'ready'
  const regStatus = worker.whatsapp_registration_status

  const isPending = isBusiness && regStatus != null && !TERMINAL_STATUSES.includes(regStatus)
  const isFailed = isBusiness && regStatus === 'failed'
  const isConnected = isBusiness && (regStatus === 'verified' || regStatus === 'not_applicable')
  const selectedProviderNeedsWaba = underlyingProvider === 'salvy' && !whatsapp_business_available
  const canOpenProviderConnect = isNone && isReady && can_manage_resources && !selectedProviderNeedsWaba
  const shouldStartWabaSignup = isNone && isReady && can_manage_resources && selectedProviderNeedsWaba
  const providerLabel = worker.whatsapp_underlying_provider === 'twilio' ? 'Twilio' : 'Meta via Salvy'
  const {
    error: embeddedSignupError,
    isConnecting: isEmbeddedSignupConnecting,
    isSdkLoading: isEmbeddedSignupSdkLoading,
    startSignup: startEmbeddedSignup,
  } = useWhatsappEmbeddedSignup({
    enabled: shouldStartWabaSignup && whatsapp_embedded_signup.ready,
    facebookAppId: whatsapp_embedded_signup.facebookAppId,
    configId: whatsapp_embedded_signup.configId,
    apiVersion: whatsapp_embedded_signup.apiVersion,
    digitalWorkerId: whatsapp_embedded_signup.digitalWorkerId,
    returnTo: whatsapp_embedded_signup.returnTo,
  })
  const embeddedSignupLoading = isEmbeddedSignupConnecting || isEmbeddedSignupSdkLoading

  const handleConnect = () => {
    if (selectedProviderNeedsWaba) return
    setConnecting(true)
    const payload = {
      whatsapp_provider: 'business',
      whatsapp_underlying_provider: underlyingProvider,
    }
    router.post(
      `/dashboard/digital_workers/${worker.id}/change_whatsapp_provider`,
      payload,
      {
        preserveScroll: true,
        onSuccess: () => setModalOpen(false),
        onFinish: () => setConnecting(false),
      },
    )
  }

  const handleDisconnect = () => {
    if (confirm('Desconectar o WhatsApp deste colaborador?')) {
      router.post(
        `/dashboard/digital_workers/${worker.id}/change_whatsapp_provider`,
        { whatsapp_provider: 'none' },
        { preserveScroll: true },
      )
    }
  }

  const handleRetry = () => {
    router.post(
      `/dashboard/digital_workers/${worker.id}/retry_provisioning`,
      {},
      { preserveScroll: true },
    )
  }

  // Connected — business (verified or not_applicable)
  if (isConnected) {
    return (
      <div className="relative rounded-xl border border-success/40 bg-success/10 p-4">
        <div className="flex items-center gap-3">
          <WhatsAppIcon className="h-8 w-8 text-[#25D366]" />
          <div>
            <p className="font-medium text-foreground">WhatsApp</p>
            <p className="text-xs text-muted-foreground">
              {providerLabel} · {worker.twilio_sender?.from_number || (worker.whatsapp_number ? formatWhatsApp(worker.whatsapp_number) : 'Business API')}
            </p>
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-success/12 px-2 py-0.5 text-xs font-medium text-success">
            <span className="w-1.5 h-1.5 rounded-full bg-success" />
            Ativo
          </span>
        </div>
        {can_manage_resources && (
          <button
            type="button"
            className="mt-3 text-xs text-destructive transition-opacity duration-150 hover:opacity-80 cursor-pointer"
            onClick={handleDisconnect}
          >
            Desconectar
          </button>
        )}
        {worker.whatsapp_underlying_provider === 'twilio' && worker.twilio_sender && (
          <div className="mt-3 grid gap-1 rounded-md bg-background/70 p-2 text-xs text-muted-foreground">
            <div>Account: <span className="font-mono">{worker.twilio_sender.subaccount_sid}</span></div>
            {worker.twilio_sender.sender_sid && <div>Sender: <span className="font-mono">{worker.twilio_sender.sender_sid}</span></div>}
          </div>
        )}
        {worker.whatsapp_underlying_provider !== 'twilio' && worker.whatsapp_phone && (
          <div className="mt-3 grid gap-1 rounded-md bg-background/70 p-2 text-xs text-muted-foreground">
            <div>WABA: <span className="font-mono">{worker.whatsapp_phone.waba.external_id}</span></div>
            <div>Phone ID: <span className="font-mono">{worker.whatsapp_phone.meta_phone_number_id}</span></div>
          </div>
        )}
      </div>
    )
  }

  // Pending — business (phone assignment in progress)
  if (isPending && !isFailed) {
    return (
      <div className="relative rounded-xl border border-warning/40 bg-warning/10 p-4">
        <div className="flex items-center gap-3">
          <WhatsAppIcon className="h-8 w-8 text-[#25D366]" />
          <div>
            <p className="font-medium text-foreground">WhatsApp</p>
            <p className="text-xs text-muted-foreground">{providerLabel} · Business API</p>
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-warning/13 px-2 py-0.5 text-xs font-medium text-warning">
            Configurando...
          </span>
        </div>
        {can_manage_resources && !worker.runtime_capabilities.whatsapp_cloud.supported && (
          <button type="button" className="mt-3 text-xs text-destructive cursor-pointer" onClick={handleDisconnect}>
            Desconectar
          </button>
        )}
      </div>
    )
  }

  // Failed — business (phone assignment failed)
  if (isFailed) {
    return (
      <div className="relative rounded-xl border border-destructive/40 bg-destructive/10 p-4">
        <div className="flex items-center gap-3">
          <WhatsAppIcon className="h-8 w-8 text-muted-foreground" />
          <div>
            <p className="font-medium text-foreground">WhatsApp</p>
            <p className="text-xs text-muted-foreground">{providerLabel} · Business API</p>
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-destructive/12 px-2 py-0.5 text-xs font-medium text-destructive">
            Falha
          </span>
        </div>
        {can_manage_resources && (
          <div className="mt-3 flex items-center gap-3">
            {worker.runtime_capabilities.whatsapp_cloud.supported && <button
              type="button"
              className="text-xs text-brand transition-colors duration-150 hover:text-brand-hover cursor-pointer"
              onClick={handleRetry}
            >
              Tentar novamente
            </button>}
            <button
              type="button"
              className="text-xs text-destructive transition-opacity duration-150 hover:opacity-80 cursor-pointer"
              onClick={handleDisconnect}
            >
              Desconectar
            </button>
          </div>
        )}
      </div>
    )
  }

  // Connected — personal (legacy)
  if (isPersonal) {
    return (
      <div className="relative rounded-xl border border-success/40 bg-success/10 p-4">
        <div className="flex items-center gap-3">
          <WhatsAppIcon className="h-8 w-8 text-[#25D366]" />
          <div>
            <p className="font-medium text-foreground">WhatsApp</p>
            <p className="text-xs text-muted-foreground">
              Pessoal{worker.whatsapp_number ? ` - ${formatWhatsApp(worker.whatsapp_number)}` : ''}
            </p>
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-success/12 px-2 py-0.5 text-xs font-medium text-success">
            <span className="w-1.5 h-1.5 rounded-full bg-success" />
            Ativo
          </span>
        </div>
        {can_manage_resources && (
          <button
            type="button"
            className="mt-3 text-xs text-destructive transition-opacity duration-150 hover:opacity-80 cursor-pointer"
            onClick={handleDisconnect}
          >
            Desconectar
          </button>
        )}
      </div>
    )
  }

  // Can connect — none, ready, has permission
  if (canOpenProviderConnect) {
    return (
      <>
        <button
          type="button"
          className="rounded-lg border border-border bg-card p-4 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-colors cursor-pointer text-left"
          onClick={() => setModalOpen(true)}
        >
          <div className="flex items-center gap-3">
            <WhatsAppIcon className="h-8 w-8 text-[#25D366]" />
            <div>
              <p className="font-medium text-foreground">WhatsApp</p>
              <p className="text-xs text-[#25D366]">Conectar</p>
            </div>
          </div>
        </button>

        <Dialog open={modalOpen} onOpenChange={(v) => !v && setModalOpen(false)}>
          <DialogContent className="max-w-[440px]">
            <DialogHeader>
              <DialogTitle>Conectar WhatsApp</DialogTitle>
            </DialogHeader>
            <Alert variant="info">
              <Info className="size-4" />
              <AlertDescription>
                O canal WhatsApp será configurado automaticamente para este colaborador.
              </AlertDescription>
            </Alert>
            {underlyingProvider === 'twilio' && (
              <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                Provider definido pelo admin: Twilio (numero + WABA).
              </div>
            )}
            {underlyingProvider === 'salvy' && (
              <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                Provider definido pelo admin: Meta via Salvy.
              </div>
            )}
            {worker.twilio_sender && (
              <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Twilio sender status</span>
                  <span className="font-mono">{worker.twilio_sender.sender_status}</span>
                </div>
                {worker.twilio_sender.from_number && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">From</span>
                    <span className="font-mono">{worker.twilio_sender.from_number}</span>
                  </div>
                )}
                {worker.twilio_sender.failure_count > 0 && (
                  <div className="text-destructive">
                    Falhas OTP: {worker.twilio_sender.failure_count}/3
                  </div>
                )}
                {worker.twilio_sender.otp_locked_until && (
                  <div className="text-destructive">
                    OTP travado até {new Date(worker.twilio_sender.otp_locked_until).toLocaleString()}
                  </div>
                )}
                {worker.twilio_sender.last_status_error && (
                  <div className="text-destructive truncate" title={worker.twilio_sender.last_status_error}>
                    {worker.twilio_sender.last_status_error}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={handleConnect}
                disabled={connecting || selectedProviderNeedsWaba}
              >
                {connecting && <Loader2 className="size-4 animate-spin" />}
                Conectar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  if (isNone && isReady && can_manage_resources && !whatsapp_business_available) {
    const embeddedSignupAvailable = whatsapp_embedded_signup.ready

    return (
      <div className="space-y-2">
        <button
          type="button"
          className={`w-full rounded-lg border border-border bg-card p-4 text-left transition-colors ${
            embeddedSignupAvailable
              ? 'cursor-pointer hover:border-[#25D366] hover:bg-[#25D366]/5'
              : 'cursor-not-allowed opacity-60'
          }`}
          onClick={startEmbeddedSignup}
          disabled={!embeddedSignupAvailable || embeddedSignupLoading}
        >
          <div className="flex items-center gap-3">
            <WhatsAppIcon className={`h-8 w-8 ${embeddedSignupAvailable ? 'text-[#25D366]' : 'text-muted-foreground'}`} />
            <div>
              <p className="font-medium text-foreground">WhatsApp</p>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                embeddedSignupAvailable ? 'bg-[#25D366]/10 text-[#128C4A]' : 'bg-muted text-muted-foreground'
              }`}>
                {embeddedSignupLoading ? 'Abrindo Meta...' : embeddedSignupAvailable ? 'Conectar WABA' : 'Configuração pendente'}
              </span>
            </div>
          </div>
        </button>
        {embeddedSignupError && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{embeddedSignupError}</AlertDescription>
          </Alert>
        )}
      </div>
    )
  }

  // Cannot connect — not ready or no permission
  return (
    <div className="rounded-lg border border-border bg-muted p-4 opacity-50 cursor-not-allowed">
      <div className="flex items-center gap-3">
        <WhatsAppIcon className="h-8 w-8 text-muted-foreground" />
        <div>
          <p className="font-medium text-foreground">WhatsApp</p>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {!isReady ? 'Aguardando provisionamento' : 'Indisponível'}
          </span>
        </div>
      </div>
    </div>
  )
}
